<template>
  <div class="mt5" style="margin-left: 20px;">
    <div v-if="getProfile.role !== 'admin'" class="pl-subheading mv2 ph4 pb3">
      <span
        >This is the suggestions page. Periodically, a member of the Parlay Team
        will look at the analytics and engagement in your school / district and
        make suggestions for how coaches and administrators can work with
        teachers to deepen engagement with the Parlay, share best practices, and
        continue to foster a culture of student-driven discussions. These
        suggestions will also be sent in email format to relevant administrators
        and coaches.</span
      >
    </div>
    <div
      v-if="getProfile.role === 'admin'"
      class="new-suggestion"
      id="new-suggestion-btn"
      style="margin-left: 30px;"
    >
      <pl-button
        @click.native="openTextEditor"
        class="pl-white justify-center"
        style="margin: 0;"
      >
        <img
          src="/static/course_assets/create_new_plus_sign.svg"
          alt=""
          class="create-new-image"
        />
        <span
          style="
            font-size: 18px;
            color: #273248;
            padding: 5px;
            font-weight: 600;
          "
          >New Suggestion</span
        >
      </pl-button>
    </div>
    <div class="tiny-mce-container" v-if="openEditor">
      <div class="suggestions-inner-container">
        <tinymce
          style="height: 80% !important; width: 100%;"
          :toolbarOptions="toolbar_options"
          :savedContent="prompt_response"
          :statusBar="false"
          :draftResponse="saved_response"
          @change="handleResponseChange"
        />

        <div
          class="flex flex-nowrap relative items-center submission-form-bottom-row mv2"
        >
          <div class="flex" style="max-width: 400px; max-height: 100px;">
            <span
              style="
                width: 100px;
                text-align: center;
                font-size: 14px;
                margin: auto;
                padding: 5px;
                font-weight: 600;
                color: #273248;
              "
              >Email To:
            </span>
            <div v-if="coachesAdmins">
              <pl-multiselect
                v-model="selectedEmails"
                :options="coachesAdmins"
                :multiple="true"
                :custom-label="nameWithEmail"
                :close-on-select="false"
                :clear-on-select="false"
                :preserve-search="true"
                :preselect-first="false"
                label="name"
                class="flex mv2"
                placeholder="Select coaches/admins"
                track-by="id"
              >
                <template
                  slot="selection"
                  slot-scope="{ values, search, isOpen }"
                >
                  <span
                    v-if="selectedEmails && selectedEmails.length &amp;&amp; !isOpen"
                    class="multiselect__single"
                  >
                    {{ selectedEmails.length }} options selected
                  </span>
                </template>
              </pl-multiselect>
            </div>
          </div>
          <div class="flex flex-grow h-100 items-center justify-end">
            <pl-button
              @click.native="closeEditor"
              class="edit-submission-exit-button b"
              v-my-tooltip="'Close editor'"
            >
              CANCEL
            </pl-button>
            <pl-button
              @click.native="submitSuggestion(true)"
              class="pl-white b"
              :disabled="is_saving || !recent_changes"
              v-my-tooltip="'Save suggestion'"
            >
              SAVE
            </pl-button>
            <pl-button
              @click.native="submitSuggestion(false)"
              class="pl-primary edit-submission-exit-button b"
              :disabled="
                is_saving ||
                (!recent_changes &&
                  !selectedSuggestionDrafted &&
                  emails_changed)
              "
              v-my-tooltip="'Send suggestion'"
            >
              SEND
            </pl-button>
          </div>
        </div>
      </div>
    </div>
    <div class="suggestions-container">
      <div
        v-for="(suggestion, index) in visible_suggestions"
        :key="index"
        class="suggestion-card shadow-3"
      >
        <suggestion-card
          :suggestion="suggestion"
          :isNewSuggestion="suggestion.isNew"
        />
      </div>
    </div>
    <dialog-delete-confirm
      v-if="show_delete_suggestion_modal"
      @close="show_delete_suggestion_modal = false"
      @actionConfirmed="confirmDeleteSuggestion"
      optionalText="Suggestion"
    />
  </div>
</template>

<script>
  import tinymce from 'components/textEditor/tinymce.vue';
  import suggestionCard from '../suggestions/suggestionCard.vue';
  import { EventBus } from 'utils/event-bus';
  import { mapGetters } from 'vuex';
  import adminServices from 'services/admin/adminServices';
  import dialogDeleteConfirm from 'components/dialogDeleteConfirm';

  export default {
    components: { suggestionCard, tinymce, dialogDeleteConfirm },
    data() {
      return {
        prompt_response: '',
        recent_changes: false,
        is_saving: false,
        selected_suggestion_id: null,
        show_delete_suggestion_modal: false,
        emails_changed: false,
        selectedEmails: [],
        editedEmails: [],
        saved_response: '',
        coachesAdmins: null,
        openEditor: false,
        toolbar_options:
          'undo redo | bold italic underline | alignleft aligncenter | removeformat | numlist bullist link |',
        suggestions: [],
      };
    },
    watch: {
      'selectedEmails.length'() {
        const editedEmailsIds = this.editedEmails.map((x) => x.id);
        const selectedEmailsIds = this.selectedEmails.map((x) => x.id);
        this.emails_changed = this.arraysAreIdentical(
          selectedEmailsIds,
          editedEmailsIds
        );
      },
      getSuggestions() {
        this.suggestions = this.getSuggestions.slice();
      },
    },
    computed: {
      ...mapGetters(['getProfile', 'getSuggestions']),
      adminCoachesEmails() {
        return this.coachesAdmins.map((x) => x.name);
      },
      visible_suggestions() {
        return this.getProfile.role === 'admin'
          ? this.suggestions
          : this.suggestions.filter((s) => !s.is_draft);
      },
      sentToEmails() {
        return this.selectedEmails.map((x) => x.email);
      },
      selectedSuggestion() {
        if (this.selected_suggestion_id) {
          return this.suggestions.find(
            (suggestion) => suggestion.id === this.selected_suggestion_id
          );
        }
      },
      selectedSuggestionDrafted() {
        if (this.selectedSuggestion) {
          return this.selectedSuggestion.is_draft;
        }
        return false;
      },
    },
    methods: {
      openTextEditor() {
        if (this.getProfile.role === 'admin' && !this.coachesAdmins)
          this.getSchoolCoachesAndAdmins();
        this.openEditor = true;
      },
      nameWithEmail({ name, email }) {
        if (name && email) {
          return `${name} (${email})`;
        }
        return `${name}`;
      },
      handleResponseChange(prompt_response) {
        // Update the prompt_response in local data
        this.prompt_response = prompt_response;
        this.recent_changes = true; // Invoke debounced function
        /*  if (this.recent_changes && !this.submissionData.start_date) {
            EventBus.$emit('submissionToSave', true);
        } */
      },
      submitSuggestion(is_draft) {
        return this.selected_suggestion_id
          ? this.saveSuggestion(is_draft)
          : this.sendSuggestion(is_draft);
      },
      closeEditor() {
        this.openEditor = false;
        this.selected_suggestion_id = null;
        this.recent_changes = false;
        this.clearEditor();
      },
      replaceSuggestion(suggestion) {
        const index = this.suggestions.findIndex(
          (sug) => sug.id === this.selected_suggestion_id
        );
        if (index >= 0) {
          this.suggestions[index] = suggestion;
        }
      },
      sendSuggestion(is_draft) {
        let date_sent;
        if(!is_draft) {
          date_sent = Date.now();
        }
        const payload = {
          content: this.prompt_response,
          is_draft,
          sent_to_emails: this.selectedEmails,
          school_id: this.$route.params.schoolID,
          author_id: this.getProfile.id,
          sent_at: date_sent
        };
        adminServices
          .createSuggestion(this.$route.params.schoolID, payload)
          .then((res) => {
            const suggestion = res.data;
            this.suggestions.unshift(suggestion);
            this.closeEditor();
            EventBus.$emit('toast-msg', 'Success! Created suggestion.');
            this.clearEditor();
          })
          .catch((error) => {
            console.log(error);
          });
      },
      scrollToTextEditor() {
        const submissionToScrollToElement = document.getElementById(
          'new-suggestion-btn'
        );
        if (submissionToScrollToElement) {
          submissionToScrollToElement.scrollIntoView({
            block: 'start',
            inline: 'nearest',
            behavior: 'smooth',
          });
        }
      },
      saveSuggestion(is_draft) {
        let date_sent;
        if(!is_draft) {
          date_sent = Date.now();
        }
        const payload = {
          content: this.prompt_response,
          is_draft,
          sent_to_emails: this.selectedEmails,
          school_id: this.$route.params.schoolID,
          author_id: this.getProfile.id,
          sent_at: date_sent
        };
        this.is_saving = true;
        adminServices
          .editSuggestion(
            this.$route.params.schoolID,
            this.selected_suggestion_id,
            payload
          )
          .then((res) => {
            const edited_suggestion = res.data;
            this.saved_response = this.prompt_response;
            this.recent_changes = false;
            this.replaceSuggestion(edited_suggestion);
            this.closeEditor();
            EventBus.$emit('toast-msg', 'Success! Saved suggestion.');
            this.clearEditor();
          })
          .catch((error) => {
            console.log(error);
          })
          .finally(() => {
            this.is_saving = false;
          });
      },

      getSchoolCoachesAndAdmins() {
        adminServices
          .getSchoolCoachesAndAdmins(this.$route.params.schoolID)
          .then((res) => {
            this.coachesAdmins = res.data;
          })
          .catch((error) => {
            console.log(error);
          });
      },
      deleteSuggestion(suggestionID) {
        this.show_delete_suggestion_modal = true;
        this.selected_suggestion_id = suggestionID;
      },
      confirmDeleteSuggestion() {
        adminServices
          .deleteSuggestion(
            this.$route.params.schoolID,
            this.selected_suggestion_id
          )
          .then(() => {
            const index = this.suggestions.findIndex(
              (suggestion) => suggestion.id === this.selected_suggestion_id
            );
            if (index >= 0) this.suggestions.splice(index, 1);
            EventBus.$emit('toast-msg', 'Success! Suggestion was deleted');
            this.selected_suggestion_id = null;
          })
          .catch((error) => {
            console.log(error);
          });
      },
      clearEditor() {
        this.saved_response = '';
        this.prompt_response = '';
        this.selectedEmails = [];
        this.selected_suggestion_id = null;
      },
      arraysAreIdentical(arr1, arr2) {
        if (arr1.length !== arr2.length) return false;
        for (var i = 0, len = arr1.length; i < len; i++) {
          if (arr1[i] !== arr2[i]) {
            return false;
          }
        }
        return true;
      },
    },
    mounted() {
      this.saved_response = this.prompt_response;

      this.$store.dispatch('setSuggestions', this.$route.params.schoolID);

      this.suggestions = this.getSuggestions.splice();
      EventBus.$on('editSuggestion', (suggestion) => {
        this.prompt_response = suggestion.content;
        this.saved_response = suggestion.content;
        this.selectedEmails = suggestion.sent_to_emails;
        this.editedEmails = suggestion.sent_to_emails;
        this.selected_suggestion_id = suggestion.id;
        this.openTextEditor();
        this.scrollToTextEditor();
      });

      EventBus.$on('deleteSuggestion', (suggestionID) => {
        this.deleteSuggestion(suggestionID);
      });
    },
    beforeDestroy() {
      EventBus.$off('editSuggestion');
      EventBus.$off('deleteSuggestion');
    },
    beforeRouteLeave(to, from, next) {
      EventBus.$emit('read-new-suggestions');
      this.$store.dispatch('cleanNewSuggestion').catch(console.error);
      next();
    },
  };
</script>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style lang="stylus">
  @import '~app/global-stylus.styl';
  .multiselect__content-wrapper
    max-width: 500px;
    min-width: fit-content;
  .multiselect
    max-width:300px;
    /deep/ .multiselect__single
      width: auto;
    /deep/ .multiselect__tags
      border: 2px solid #e1e8ee;
      width: 100%;
    /deep/ .multiselect__tag
      background-color: #d8d8d8;
      border-radius: 12px;
      height: 25px;
      max-width: 250px
      color: rgba(41, 41, 41, 0.75);
      font-size: 18px;
      letter-spacing: 0;
      line-height: 25px;
      padding: 0 26px 0 10px;
      i
        right: 6px;
        top: 6px;
        bottom: 0;
        font-weight: 700;
        font-style: normal;
        width: 14px;
        text-align: center;
        line-height: 6px;
        transition: all 0.2s ease;
        border-radius: 50%;
        height: 14px;
        padding-top: 3px;
        background-color: #787878;
        color: #d8d8d8;
        &::after
          color: #d8d8d8;
  .suggestions-inner-container
    height: 80%
    width: 90%
    position: absolute
    top:5%
    left:5%
    .tox-tinymce
        .tox-editor-container
            height: 310px !important
            border-radius:5px
            border: 1px solid black !important
            margin-bottom: 1px !important;
            background-color: #fff !important;
  span[data-selected='Selected']
    span
      margin-right:70px;
  .mce-tinymce
    height: 80% !important;
    border-radius:5px
    border: 1px solid black !important
    padding: 4px !important;
    margin-bottom: 1px !important;
  .suggestion-card
      margin: 30px
      height: fit-content
  .tiny-mce-container
     height : 400px
     margin: 30px
     position:relative
     border-radius:5px
     box-shadow: 2px 2px 4px 2px rgba(0, 0, 0, 0.2);
     background: rgba(114, 204, 215,0.2)
</style>



// WEBPACK FOOTER //
// suggestionsTab.vue?246d713c